import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { QuizFinalLoader } from "@/components/QuizFinalLoader";
import { logger } from "@/lib/logger";

const MIN_LOADER_TIME = 6000; // 6 seconds

const Result = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(true);
  
  useEffect(() => {
    const { score, band, answers, quizSubmissionId, websiteScan, foundLinkedInProfile, interstitialAnswers } = location.state || {};
    
    // If no quiz data, redirect to home
    if (typeof score !== "number" || !band) {
      navigate("/", { replace: true });
      return;
    }
    
    const processQuizResults = async () => {
      // Track headline conversion
      const sessionId = localStorage.getItem('headline_session_id');
      const variantId = localStorage.getItem('assigned_variant_id');
      
      if (sessionId && variantId) {
        try {
          await supabase.from('headline_conversions').insert({
            variant_id: variantId,
            session_id: sessionId,
            quiz_submission_id: quizSubmissionId || null
          });
          console.log('✅ Quiz completion conversion tracked');
        } catch (error) {
          console.error('❌ Error tracking conversion:', error);
        }
      }

      // Push quiz completion to GTM dataLayer
      if (typeof window !== 'undefined' && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'quiz_completed',
          quiz_score: score,
          quiz_band: band,
          quiz_submission_id: quizSubmissionId || undefined,
          event_category: 'quiz',
          event_label: `Score: ${score} | Band: ${band}`
        });
        console.log('✅ GTM quiz completion event pushed');
      }

      // Generate AI analysis immediately
      if (quizSubmissionId) {
        try {
          logger.info('🤖 Starting AI analysis generation...');
          
          // PRIORITY 2: Fetch complete quiz data from database
          const { data: enrichedQuiz } = await supabase
            .from('quiz_submissions')
            .select('website_scan, competitor_analysis, enriched_data')
            .eq('id', quizSubmissionId)
            .single();
          
          const quizAnswersWithMeta: any = {
            ...(answers || {}),
            ...(foundLinkedInProfile ? { found_linkedin_profile: foundLinkedInProfile } : {}),
          };

          const quizData = {
            answers: quizAnswersWithMeta,
            websiteScan: enrichedQuiz?.website_scan || websiteScan || {},
            competitorAnalysis: enrichedQuiz?.competitor_analysis || {},
            interstitialAnswers: interstitialAnswers || {}
          };

          const { data, error } = await supabase.functions.invoke('generate-quiz-recommendations', {
            body: { quizData, band, quizSubmissionId }
          });

          if (error) {
            console.error('Error generating AI analysis:', error);
            logger.error('AI analysis generation failed', error);
          } else if (data?.analysis) {
            logger.info('✅ AI analysis generated and saved by edge function');
            console.log('✅ AI analysis saved to database by edge function');
          }
        } catch (err) {
          console.error('Exception during AI generation:', err);
          logger.error('AI generation exception', err);
        }
      }

      setIsGenerating(false);

      // Ensure minimum loader time
      const minTimePromise = new Promise(resolve => setTimeout(resolve, MIN_LOADER_TIME));
      await minTimePromise;

      // Navigate to report
      navigate(`/raport-${band}`, {
        state: { score, band, answers, quizSubmissionId, websiteScan, foundLinkedInProfile, interstitialAnswers },
        replace: true
      });
    };

    processQuizResults();
  }, [location.state, navigate]);
  
  // Always render the loader
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <QuizFinalLoader />
    </div>
  );
};

export default Result;
